import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * useLoanNumber
 *
 * The borrower's own file reference, so they can quote it to support instead of
 * spelling an email address down a phone line.
 *
 * ⚠️ ONE NUMBER, TWO PEOPLE. loan_number identifies a FILE, not a person — a
 * joint application is one file with two borrowers, and the co-borrower
 * deliberately inherits the primary's number when they accept the invite. That
 * is why `isJoint` comes back with it: a borrower who is told up front that the
 * number is shared is not surprised when support pulls up a file with two names
 * on it. Any support flow keyed on this number must expect more than one row.
 *
 * ⚠️ SHARED SO IT IS FETCHED ONCE PER SURFACE, NOT PER RENDER. Both the account
 * menu and the dashboard show it; a component-local fetch in each would mean two
 * requests for one unchanging string.
 *
 * Fails quiet by design: this is a convenience, and a support reference that
 * errors is worse than one that is simply absent. Callers render nothing when
 * `loanNumber` is null.
 *
 * @returns {{ loanNumber: string|null, isJoint: boolean, role: string|null, loading: boolean }}
 */
export function useLoanNumber() {
  const { token } = useAuth();
  const [state, setState] = useState({ loanNumber: null, isJoint: false, role: null, loading: true });

  useEffect(() => {
    if (!token) {
      setState({ loanNumber: null, isJoint: false, role: null, loading: false });
      return undefined;
    }

    // Guards against a state update after unmount — this resolves after a
    // network round trip and the account menu unmounts as soon as a drawer
    // closes.
    let alive = true;

    (async () => {
      try {
        const res = await fetch(`${API}/api/borrower/loan-number`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!alive) return;
        setState({
          loanNumber: data.loan_number || null,
          isJoint: data.is_joint === true,
          role: data.role || null,
          loading: false,
        });
      } catch (_) {
        // Quiet on purpose — see above.
        if (alive) setState({ loanNumber: null, isJoint: false, role: null, loading: false });
      }
    })();

    return () => { alive = false; };
  }, [token]);

  return state;
}
